import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  PaginatedResponse,
  RefundTemplateAction,
  RefundTemplateDraftInput,
  RefundTemplateEvent,
  RefundTemplateVersion,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

const base = '/admin/contract-templates/workflows/refund';
const rootKey = [...queryKeys.contractTemplates.all, 'refund-workflow'] as const;
const detailKey = (id: string) => [...rootKey, 'detail', id] as const;

export function useRefundTemplateVersion(id?: string) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<ApiSuccess<RefundTemplateVersion>>(`${base}/${id}`)).data.data,
  });
}

export function useRefundTemplateHistory(id: string | undefined, page: number, enabled: boolean) {
  const limit = 10;
  return useQuery({
    queryKey: [...rootKey, 'history', id ?? '', page],
    enabled: Boolean(id) && enabled,
    queryFn: async (): Promise<PaginatedResponse<RefundTemplateEvent>> => {
      const {
        data: { data },
      } = await apiClient.get<ApiSuccess<{ count: number; rows: RefundTemplateEvent[] }>>(
        `${base}/${id}/history`,
        { params: { page, limit } },
      );
      return {
        items: data.rows,
        total: data.count,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(data.count / limit)),
      };
    },
  });
}

export type RefundTemplateCommand =
  | { action: 'create'; payload: RefundTemplateDraftInput }
  | {
      action: 'update';
      id: string;
      payload: { name: string; templateHtml: string; expectedRevision: number };
    }
  | { action: RefundTemplateAction; id: string; payload: { expectedRevision: number } };

export function useRefundTemplateCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (command: RefundTemplateCommand): Promise<RefundTemplateVersion> => {
      const response =
        command.action === 'update'
          ? await apiClient.patch<ApiSuccess<RefundTemplateVersion>>(
              `${base}/${command.id}`,
              command.payload,
            )
          : await apiClient.post<ApiSuccess<RefundTemplateVersion>>(
              command.action === 'create' ? base : `${base}/${command.id}/${command.action}`,
              command.payload,
            );
      return response.data.data;
    },
    retry: false,
    onSuccess: (data) => {
      qc.setQueryData(detailKey(data.id), data);
      void qc.invalidateQueries({ queryKey: queryKeys.contractTemplates.all });
    },
  });
}
